import { DropdownOption, RolodexUserMetadata } from 'types'
import { v4 as uuid4 } from 'uuid'

export const INVALID_ENTRY_ID_REGEX = new RegExp(
  /[^A-Za-z0-9$\-.+!*'(), ]+/,
  'g'
)
const SPACE_AND_HYPHEN_REGEX = new RegExp(/[ -]+/, 'g')
/**
 * Function to generate a new entry slug from provided parameters. Should be paired with isValidEntrySlug to ensure
 * that the generated slug is going to be accepted by Rolodex.
 * @param entryName   The name provided by users (such as organization name). If none provided, the date is used
 * @param hash        A hash (truncated to six digits). Optionally provided as a parameter so it's not re-generated every call
 */
export const generateEntrySlug = (
  entryName: string | undefined = undefined,
  hash: string | undefined = undefined
): string => {
  let entrySlug: string
  if (!entryName) {
    // If no name was provided, generate a date (such as for inspections; format is YYYYMMDD)
    entrySlug = new Date().toISOString().split('T')[0].replaceAll('-', '')
  } else {
    // Otherwise, use the provided name, but modify it to align with current standards
    entrySlug = entryName
      .replaceAll(SPACE_AND_HYPHEN_REGEX, '-')
      .replaceAll(INVALID_ENTRY_ID_REGEX, '')
      .toLowerCase()
  }

  // Truncate the slug so that we can append the hash at the end
  if (entrySlug.length > 68) {
    entrySlug.slice(0, 68)
  }

  // If a hash was provided, slice it to 6 chars (to align with standards)
  // If no hash was provided, generate one
  const entryHash = hash ? hash.slice(0, 6) : uuid4().slice(0, 6)

  // Check to see if the entry slug already ends with a hyphen. If so, don't add one before the hash
  if (entrySlug.endsWith('-')) {
    entrySlug += entryHash
  } else {
    entrySlug += `-${entryHash}`
  }

  return entrySlug
}
/**
 * Function to validate provided entry slugs against what is accepted in Rolodex. Allows us to provide feedback to
 * the user / developer if the generated slug will not be accepted
 * @param entrySlug     The string ID that will be passed to rolodex
 */
export const isValidEntrySlug = (entrySlug: string): boolean => {
  return !entrySlug.match(INVALID_ENTRY_ID_REGEX)
}

export const getGeckoUserOptions = (
  userData: RolodexUserMetadata[] | undefined
): Array<DropdownOption> => {
  return (userData || [])
    .filter((user) => user.email && user.email !== user.name) // Filter out users without an email or with the same email as name
    .map((user) => ({
      value: user.email as string,
      label: user.name,
    }))
}
